/**
 * Pool Reports Contribution Page
 *
 * Displays contribution data with owner details, vessel metrics, and allocation information.
 * Table layout aligned with Net allocation (borders, sticky owner column, row highlight).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useContributions } from '@/hooks/usePoolReports';
import { usePoolReportsStore } from '@/store';
import { formatNumber } from '@/utils/formatters';
import { CSVService } from '@/utils/csvExport';
import { Download, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui';
import { PoolReportTableCard } from '@/components/poolReports/PoolReportTableCard';
import {
  poolReportOwnerLabelClass,
  poolReportOwnerLinkClass,
  poolReportSelectedCellClass,
} from '@/components/poolReports/poolReportTableHighlight';
import {
  POOL_REPORT_OWNER_COL_HEADER,
  poolReportTheadMutedClass,
  poolReportTheadStickyClass,
  poolReportThEmphasisClass,
  poolReportThUnitClass,
  poolReportTdPadClass,
} from '@/components/poolReports/poolReportTableChrome';
import type { Contribution } from '@/types/poolReportsExtended.types';

const ownerStickyMd =
  'md:sticky md:left-0 md:z-10 md:min-w-[12rem] md:max-w-[min(40vw,24rem)] md:shadow-sm md:align-top';

export function PoolReportsContributionPage() {
  const navigate = useNavigate();
  const { selectedReport, reportType, selectedPoolId } = usePoolReportsStore();
  const fileId = selectedReport?.fileId;
  const [highlightedRowKey, setHighlightedRowKey] = useState<string | null>(null);

  const { data: contributions = [], isLoading } = useContributions(fileId || 0, !!fileId);

  const handleExport = () => {
    if (!selectedReport || !contributions.length) return;

    const rows = contributions.map((item) => [
      item.ownerName || '',
      formatNumber(item.dwt || 0, 0),
      formatNumber(item.averageAgeInTIFleet || 0, 2),
      formatNumber(item.onhirePeriod || 0, 2),
      formatNumber(item.offhirePeriod || 0, 2),
      formatNumber(item.contributionAfterParticipantAllocationPeriod || 0, 0),
      formatNumber(item.allocationInclParticipantAllocationPeriod || 0, 0),
      formatNumber(item.allocationInclParticipantAllocationRPD || 0, 0),
    ]);

    const headers = [
      '',
      'DWT (mts)',
      'AV AGE IN TI FLEET (Years)',
      'ON-HIRE PERIOD (Days)',
      'OFFHIRE PERIOD (Days)',
      'CONTRIBUTION PERIOD TO DATE (USD)',
      'ALLOCATION PERIOD TO DATE (USD)',
      'ALLOCATION EXPRESSED AS RATE PER DAY (USD)',
    ];

    CSVService.export(rows, `${selectedReport.description}-contribution-report`, headers);
  };

  const handleOwnerClick = (contribution: Contribution) => {
    if (!contribution.withLink) return;
    navigate(
      `/reports/${reportType}/contribution/owner/${contribution.ownerId}?poolId=${selectedPoolId}&fileId=${fileId}&ownerName=${encodeURIComponent(contribution.ownerName || '')}`
    );
  };

  const contributionTableStyles = `
    @media (min-width: 768px) {
      .contribution-report-table th:first-child,
      .contribution-report-table td:first-child {
        position: sticky;
        left: 0;
        min-width: 12rem;
        max-width: min(40vw, 24rem);
        border-right: solid 1px rgb(229 231 235);
        white-space: normal;
        word-break: break-word;
        overflow-wrap: anywhere;
        vertical-align: top;
        box-shadow: 2px 0 6px rgba(15, 23, 42, 0.06);
        clip-path: inset(0px -10px 0px 0px);
        z-index: 9;
      }
      .contribution-report-table td:first-child {
        font-weight: bold;
        text-transform: uppercase;
      }
      .dark .contribution-report-table th:first-child,
      .dark .contribution-report-table td:first-child {
        border-right: solid 1px rgb(51 65 85);
        box-shadow: 2px 0 8px rgba(0, 0, 0, 0.35);
      }
      .contribution-report-table thead th:first-child {
        z-index: 30;
      }
    }
    @media (max-width: 767px) {
      .contribution-report-table tbody td:first-child {
        position: static !important;
        left: auto !important;
        min-width: 11rem;
        max-width: none;
        white-space: normal;
        word-break: break-word;
        overflow-wrap: anywhere;
        overflow: visible;
        vertical-align: top;
        font-size: 0.875rem;
        line-height: 1.4;
        padding: 0.5rem 0.65rem;
        border-right: 1px solid rgb(229 231 235);
        box-shadow: none !important;
        clip-path: none !important;
      }
      .contribution-report-table thead th:first-child {
        position: static !important;
        left: auto !important;
        min-width: 11rem;
        max-width: none;
        white-space: normal;
        word-break: break-word;
        overflow-wrap: anywhere;
        overflow: visible;
        vertical-align: top;
        border-right: 1px solid rgb(229 231 235);
        box-shadow: none !important;
        clip-path: none !important;
      }
      .dark .contribution-report-table thead th:first-child,
      .dark .contribution-report-table tbody td:first-child {
        border-right-color: rgb(51 65 85);
      }
    }
  `;

  if (isLoading) {
    return (
      <div className="pool-contribution-page space-y-4">
        <style>{contributionTableStyles}</style>
        <PoolReportTableCard
          eyebrow="Contribution"
          title="Owner summary"
          description="Fleet size, hire periods, contribution and allocation by owner."
          endAdornment={
            <div className="h-9 w-full max-w-[10rem] animate-pulse rounded-full bg-gray-200 dark:bg-dark-700 sm:w-36" />
          }
        >
          <div className="report-table-shell">
            <table className="contribution-report-table report-table relative">
              <thead className={poolReportTheadMutedClass}>
                <tr>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <th key={i} className={poolReportThEmphasisClass}>
                      <SkeletonBar />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-dark-900">
                {Array.from({ length: 6 }).map((_, r) => (
                  <tr key={r} className="border-t border-gray-200 dark:border-dark-700">
                    {Array.from({ length: 8 }).map((_, c) => (
                      <td key={c} className="px-2.5 py-2 sm:px-3 sm:py-2.5">
                        <SkeletonBar narrow={c !== 0} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PoolReportTableCard>
      </div>
    );
  }

  return (
    <div className="pool-contribution-page space-y-4">
      <style>{contributionTableStyles}</style>
      <PoolReportTableCard
        eyebrow="Contribution"
        title="Owner summary"
        description="Fleet size, hire periods, contribution and allocation by owner. Select a name with the link icon for vessel detail."
        endAdornment={
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExport}
            leftIcon={<Download className="h-4 w-4" />}
            disabled={isLoading || contributions.length === 0}
            className="w-full rounded-full px-4 shadow-sm sm:w-auto"
          >
            Export to Excel
          </Button>
        }
      >
        <div className="report-table-shell">
          <table className="contribution-report-table report-table relative">
            <thead className={poolReportTheadStickyClass}>
              <tr>
                <th
                  className={clsx(poolReportThEmphasisClass, 'bg-gray-100/95 dark:bg-dark-900/95')}
                >
                  {POOL_REPORT_OWNER_COL_HEADER}
                </th>
                <th className={poolReportThEmphasisClass}>
                  <div>
                    DWT <span className={poolReportThUnitClass}>mts</span>
                  </div>
                </th>
                <th className={poolReportThEmphasisClass}>
                  <div>
                    AV Age in
                    <br />
                    TI Fleet <span className={poolReportThUnitClass}>Years</span>
                  </div>
                </th>
                <th className={poolReportThEmphasisClass}>
                  <div>
                    On-hire
                    <br />
                    Period <span className={poolReportThUnitClass}>Days</span>
                  </div>
                </th>
                <th className={poolReportThEmphasisClass}>
                  <div>
                    Offhire
                    <br />
                    Period <span className={poolReportThUnitClass}>Days</span>
                  </div>
                </th>
                <th
                  className={clsx(
                    poolReportThEmphasisClass,
                    'bg-primary-50/50 dark:bg-primary-950/25'
                  )}
                >
                  <div>
                    CONTRIBUTION PERIOD TO DATE <span className={poolReportThUnitClass}>USD</span>
                  </div>
                </th>
                <th
                  className={clsx(
                    poolReportThEmphasisClass,
                    'bg-primary-50/50 dark:bg-primary-950/25'
                  )}
                >
                  <div>
                    ALLOCATION PERIOD TO DATE <span className={poolReportThUnitClass}>USD</span>
                  </div>
                </th>
                <th className={poolReportThEmphasisClass}>
                  <div>
                    ALLOCATION EXPRESSED AS RATE PER DAY{' '}
                    <span className={poolReportThUnitClass}>USD</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {contributions.map((item, index) => {
                const isTotal = index === contributions.length - 1 && !item.ownerName;
                const isFirstRow = index === 0;
                const isBoldSubtotal = !item.withLink && !isTotal;
                const isClickable = item.withLink && !isTotal;
                const rowKey = isTotal ? '__total__' : String(item.ownerId ?? `idx-${index}`);
                const isHighlighted = !isTotal && highlightedRowKey === rowKey;
                const hi = poolReportSelectedCellClass(isHighlighted);

                const allocationRpd = item.allocationInclParticipantAllocationRPD ?? 0;
                const isAdjustmentsRow = (item.ownerName || '')
                  .toLowerCase()
                  .includes('adjustments total');

                const pad = `${poolReportTdPadClass} ` + (isFirstRow ? 'border-t-2 ' : '');
                const num = `${pad}tabular-nums `;

                const ownerTd = (() => {
                  if (isTotal) {
                    return clsx(
                      pad,
                      'border-gray-300 bg-gray-100 text-gray-900 dark:border-dark-600 dark:bg-dark-800 md:border-l-[3px] md:border-primary-600 dark:md:border-primary-500 ',
                      ownerStickyMd,
                      hi
                    );
                  }
                  return clsx(
                    pad,
                    'border-gray-200 bg-white text-gray-900 dark:border-dark-700 dark:bg-dark-900 md:bg-white dark:md:bg-dark-900 ',
                    ownerStickyMd,
                    hi
                  );
                })();

                const defaultNumTd = clsx(
                  num,
                  isTotal
                    ? 'border-gray-300 bg-gray-100/90 font-bold text-gray-900 dark:border-dark-600 dark:bg-dark-800/90'
                    : 'border-gray-200 bg-white text-gray-900 dark:border-dark-700 dark:bg-dark-900',
                  isBoldSubtotal && !isTotal && 'font-bold',
                  hi
                );

                const primaryNumTd = clsx(
                  num,
                  isTotal
                    ? 'border-gray-300 bg-gray-100/90 font-bold text-gray-900 dark:border-dark-600 dark:bg-dark-800/90'
                    : 'border-gray-200 bg-primary-50/35 font-semibold text-gray-900 dark:border-dark-700 dark:bg-primary-950/20',
                  isBoldSubtotal && !isTotal && 'font-bold',
                  hi
                );

                return (
                  <tr
                    key={index}
                    onClick={() => {
                      if (isTotal) return;
                      setHighlightedRowKey((prev) => (prev === rowKey ? null : rowKey));
                    }}
                    className={clsx(
                      isTotal &&
                        'bg-gradient-to-r from-gray-100 to-gray-50/95 dark:from-dark-700/80 dark:to-dark-800/60',
                      !isTotal && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-800'
                    )}
                  >
                    <td className={ownerTd}>
                      {isClickable ? (
                        <div className="flex w-full min-w-0 flex-row items-center justify-end gap-2">
                          <button
                            type="button"
                            className={clsx(
                              poolReportOwnerLabelClass,
                              poolReportOwnerLinkClass,
                              'min-w-0 flex-1 text-right'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOwnerClick(item);
                            }}
                          >
                            {item.ownerName}
                          </button>
                          <ExternalLink
                            className="size-[1.125rem] shrink-0 text-gray-500 dark:text-gray-400"
                            aria-hidden
                          />
                        </div>
                      ) : (
                        <span
                          className={clsx(poolReportOwnerLabelClass, isBoldSubtotal && 'font-bold')}
                        >
                          {item.ownerName || (isTotal ? 'Total' : '')}
                        </span>
                      )}
                    </td>
                    <td className={defaultNumTd}>{formatNumber(item.dwt, 0, 0, true)}</td>
                    <td className={defaultNumTd}>
                      {formatNumber(item.averageAgeInTIFleet, 2, 2, true)}
                    </td>
                    <td className={defaultNumTd}>{formatNumber(item.onhirePeriod, 2, 2, true)}</td>
                    <td className={defaultNumTd}>{formatNumber(item.offhirePeriod, 2, 2, true)}</td>
                    <td className={primaryNumTd}>
                      {formatNumber(item.contributionAfterParticipantAllocationPeriod, 0, 0, true)}
                    </td>
                    <td className={primaryNumTd}>
                      {formatNumber(item.allocationInclParticipantAllocationPeriod, 0, 0, true)}
                    </td>
                    <td className={defaultNumTd}>
                      {formatNumber(allocationRpd, 0, 0, !isAdjustmentsRow)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </PoolReportTableCard>
    </div>
  );
}

function SkeletonBar({ narrow }: { narrow?: boolean }) {
  return (
    <div
      className={clsx(
        'h-4 animate-pulse rounded bg-gray-200 dark:bg-dark-700',
        narrow ? 'ml-auto w-16' : 'w-20'
      )}
    />
  );
}
